import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Copy, Send, Loader2, Sparkles, RefreshCw, Paperclip, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGS = [
  { id: "en", label: "English", flag: "🇺🇸" },
  { id: "pt", label: "Português", flag: "🇧🇷" },
  { id: "es", label: "Español", flag: "🇪🇸" },
];
const TONES = [
  { id: "consultative", label_en: "Consultative", label_pt: "Consultivo", label_es: "Consultivo" },
  { id: "direct", label_en: "Direct", label_pt: "Direto", label_es: "Directo" },
  { id: "friendly", label_en: "Friendly", label_pt: "Amigável", label_es: "Amigable" },
  { id: "executive", label_en: "Executive", label_pt: "Executivo", label_es: "Ejecutivo" },
];

const AI_SUGGESTIONS = {
  en: ["Make it shorter", "Add urgency", "More data-driven", "Softer ask", "Add case-study bullet"],
  pt: ["Mais curto", "Adicionar urgência", "Mais data-driven", "Abordagem mais suave", "Incluir case"],
  es: ["Más corto", "Añadir urgencia", "Más orientado a datos", "Más suave", "Incluir caso"],
};

// Prebuilt niche templates — same source as Kanban Tools → Sequence Library.
// Duplicated here (small, high-impact) to avoid cross-module imports.
const EMAIL_TEMPLATES = [
  { id: "roofing",  niche: "Roofing", subject: "Quick question about {{company}}'s roof jobs",
    body: `Hi {{firstName}},

Saw {{company}} on a few active permits in {{city}}. Most roofers in your zip are bleeding 20-30% of incoming calls because the site loads slow on mobile.

Mind if I send a 90s audit of {{website}} for free? If it's useful we talk, if not you kept a PDF.

— Carlos` },
  { id: "hvac", niche: "HVAC", subject: "Cooler leads for {{company}} this month",
    body: `Hey {{firstName}},

AC season is starting. We helped 3 HVAC shops in {{city}} jump from 40 to 120 calls/mo in 6 weeks using Google Maps + AI audits.

Want the teardown we did for {{competitor}} as inspiration? Zero strings.

— Carlos` },
  { id: "solar", niche: "Solar", subject: "{{company}} + 18 qualified solar leads this week",
    body: `Hi {{firstName}},

We've been pulling 15-25 qualified solar homeowners in {{city}} every week for installers like yours — without spend on ads.

Takes 10min to show you. Open tomorrow at 2pm?

— Carlos` },
  { id: "gc", niche: "General Contractor", subject: "{{bidName}} bid — are you pricing it?",
    body: `Hi {{firstName}},

Noticed {{company}} wasn't on the {{bidName}} ({{value}}) bid list yet. It matches your last 3 projects.

We surface 40+ matching public bids per month. Worth a peek?

— Carlos` },
  // Iter51 — "Lead not found" cold email used when a lead's email/phone is
  // missing from the enrichment lookup. Asks the recipient (or assistant /
  // gatekeeper) for the missing contact info, optionally attaches a CSV.
  { id: "lead_not_found", niche: "Lead Not Found",
    subject: "Helping {{company}} — couldn't find the right contact",
    body: `Hi {{firstName}},

I tried reaching the right person at {{company}} but couldn't pin down their direct email — sorry for the workaround.

If you're not the right contact for prospecting / lead-gen, would you mind forwarding this to whoever owns marketing? Or send back the email and I'll go straight to them.

I attached a quick PDF of how we helped 3 firms in {{city}} fill their pipeline last quarter — no commitment, take a look when you get a sec.

— Carlos` },
];

function applyPlaceholders(text, lead) {
  if (!lead) return text;
  const firstName = (lead.owner_name || lead.name || "").split(" ")[0] || "there";
  const city = (lead.address || "").split(",")[1]?.trim() || lead.city || "your city";
  return (text || "")
    .replace(/\{\{company\}\}/g, lead.name || "your company")
    .replace(/\{\{firstName\}\}/g, firstName)
    .replace(/\{\{city\}\}/g, city)
    .replace(/\{\{website\}\}/g, lead.website || "your site")
    .replace(/\{\{competitor\}\}/g, "your competitor")
    .replace(/\{\{bidName\}\}/g, "the open RFQ")
    .replace(/\{\{value\}\}/g, "$500k");
}

export default function EmailModal({ open, onOpenChange, lead, email, loading, onRegenerate }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [language, setLanguage] = useState("en");
  const [tone, setTone] = useState("consultative");
  const [enhancing, setEnhancing] = useState(false);
  const [instruction, setInstruction] = useState("");
  // Iter51 — generic file attachment slot. Used by the "Lead Not Found"
  // template + any other case where the user wants to attach a PDF / CSV
  // (handout, audit, prospect list). For now we surface the file name +
  // size in the modal; integration with Gmail send requires the mail
  // provider so we display an inline note.
  const [attachment, setAttachment] = useState(null);
  const [to, setTo] = useState("");
  const [discovering, setDiscovering] = useState(false);
  const [discoverySource, setDiscoverySource] = useState("");

  useEffect(() => {
    if (email) {
      setSubject(email.subject || "");
      setBody(email.body || "");
      if (email.language) setLanguage(email.language);
      if (email.tone) setTone(email.tone);
    }
  }, [email]);

  // Auto-discover recipient email when the lead has none (scrapes the lead's
  // website; falls back to Apify Contact Scraper, Hunter, Apollo).
  useEffect(() => {
    if (!open || !lead) return;
    setDiscoverySource("");
    if (lead.email) { setTo(lead.email); return; }
    setTo("");
    if (!lead.website && !lead.name) return;
    setDiscovering(true);
    axios.post(`${API}/leads/discover-email`, {
      lead_id: lead.id || null, website: lead.website || "", name: lead.name || "",
    }).then(({ data }) => {
      if (data?.email) {
        setTo(data.email);
        setDiscoverySource(data.source || "");
        if (lead) lead.email = data.email;
        toast.success(language === "pt"
          ? `E-mail encontrado (${data.source === "website" ? "site do lead" : data.source}): ${data.email}`
          : `Email found via ${data.source}: ${data.email}`);
      }
    }).catch(() => {}).finally(() => setDiscovering(false));
  }, [open, lead?.id]); // eslint-disable-line

  const regenerate = useCallback((newLang, newTone) => {
    // Delegate re-generation to the parent so it can pass the full lead
    // context (pain_points, score, etc.).
    if (typeof onRegenerate === "function") {
      onRegenerate({ language: newLang ?? language, tone: newTone ?? tone });
    }
  }, [onRegenerate, language, tone]);

  const enhance = async (preset) => {
    const text = (preset || instruction || "").trim();
    if (!text) {
      toast.error("Type an AI instruction (e.g. 'shorter')");
      return;
    }
    setEnhancing(true);
    try {
      const { data } = await axios.post(`${API}/enhance-email`, {
        subject, body, instruction: text, language, tone,
        lead_name: lead?.name || "",
      });
      if (data?.email) {
        setSubject(data.email.subject || subject);
        setBody(data.email.body || body);
      }
      setInstruction("");
      toast.success("AI rewrite applied");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setEnhancing(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
    toast.success("Email copied to clipboard");
  };

  const [sending, setSending] = useState(false);

  const sendResend = async () => {
    if (!to.trim()) {
      toast.error(language === "pt" ? "Lead sem e-mail. Adicione um antes de enviar." : language === "es" ? "Lead sin email." : "Lead has no email.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("to", to.trim());
      fd.append("subject", subject);
      fd.append("body", body);
      if (lead?.id) fd.append("lead_id", lead.id);
      if (attachment?.file) fd.append("attachment", attachment.file);
      const { data } = await axios.post(`${API}/email/resend/send-form`, fd);
      toast.success(language === "pt"
        ? `Enviado · ${data.message_id?.slice(0, 8) || ""}`
        : language === "es"
        ? `Enviado · ${data.message_id?.slice(0, 8) || ""}`
        : `Sent · ${data.message_id?.slice(0, 8) || ""}`);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setSending(false); }
  };

  const sendGmail = async () => {
    if (!to.trim()) {
      toast.error(language === "pt" ? "Lead sem e-mail. Adicione um antes de enviar." : "Lead has no email.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("to", to.trim());
      fd.append("subject", subject);
      fd.append("body", body);
      if (lead?.id) fd.append("lead_id", lead.id);
      const { data } = await axios.post(`${API}/email/gmail/send-form`, fd);
      toast.success(language === "pt"
        ? `Enviado via Gmail (${data.from})`
        : `Sent via Gmail (${data.from})`);
      onOpenChange?.(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(detail);
      // Graceful fallback: open the user's local mail client pre-filled.
      if (err?.response?.status === 503) {
        window.open(`mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
      }
    } finally { setSending(false); }
  };

  const uiLabel = (t) => language === "pt" ? t.label_pt : language === "es" ? t.label_es : t.label_en;
  const headerText = {
    en: { title: "Personalized Cold Email", sub: "AI-generated · Edit before sending",
          drafting: "Drafting email with GPT…",
          lang: "Language", tone: "Tone", subject: "Subject", body: "Body",
          copy: "Copy Email", send: "Send via Gmail", send_resend: "Send via Resend",
          regen: "Regenerate", ai_help: "AI Help — rewrite with a command",
          ai_placeholder: "e.g. 'make it shorter' or 'add a meeting CTA'",
          ai_apply: "Apply", ai_presets: "Quick presets" },
    pt: { title: "Cold Email Personalizado", sub: "Gerado por IA · Edite antes de enviar",
          drafting: "Gerando email com GPT…",
          lang: "Idioma", tone: "Tom", subject: "Assunto", body: "Corpo",
          copy: "Copiar email", send: "Enviar via Gmail", send_resend: "Enviar via Resend",
          regen: "Regerar", ai_help: "Ajuda IA — reescreva com um comando",
          ai_placeholder: "ex.: 'mais curto' ou 'adicionar CTA de reunião'",
          ai_apply: "Aplicar", ai_presets: "Presets rápidos" },
    es: { title: "Cold Email Personalizado", sub: "Generado por IA · Edite antes de enviar",
          drafting: "Generando email con GPT…",
          lang: "Idioma", tone: "Tono", subject: "Asunto", body: "Cuerpo",
          copy: "Copiar email", send: "Enviar por Gmail", send_resend: "Enviar por Resend",
          regen: "Regenerar", ai_help: "Ayuda IA — reescriba con un comando",
          ai_placeholder: "ej.: 'más corto' o 'añadir CTA de reunión'",
          ai_apply: "Aplicar", ai_presets: "Atajos" },
  }[language];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="email-modal"
        className="max-w-2xl max-h-[92vh] overflow-y-auto bg-white p-0 rounded-xl border-[#E5E7EB]"
      >
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-[#E5E7EB]">
          <DialogTitle className="text-[18px] font-bold text-[#111827]">
            {headerText.title} — {lead?.name}
          </DialogTitle>
          <p className="text-[12px] text-[#6B7280] mt-1">{headerText.sub}</p>
        </DialogHeader>

        <div className="px-6 py-5 space-y-4">
          {/* Language + Tone toggles */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10.5px] font-bold uppercase tracking-wide text-[#6B7280] mb-1.5">
                {headerText.lang}
              </label>
              <div className="grid grid-cols-3 gap-1" data-testid="email-lang-toggle">
                {LANGS.map((l) => {
                  const active = language === l.id;
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => { setLanguage(l.id); regenerate(l.id, tone); }}
                      data-testid={`email-lang-${l.id}`}
                      className={
                        "h-9 rounded-lg text-[12px] font-bold transition-colors border inline-flex items-center justify-center gap-1 " +
                        (active
                          ? "bg-[#3B82F6] text-white border-[#3B82F6]"
                          : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#3B82F6]")
                      }
                    >
                      <span>{l.flag}</span> {l.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-[10.5px] font-bold uppercase tracking-wide text-[#6B7280] mb-1.5">
                {headerText.tone}
              </label>
              <div className="grid grid-cols-4 gap-1" data-testid="email-tone-toggle">
                {TONES.map((tn) => {
                  const active = tone === tn.id;
                  return (
                    <button
                      key={tn.id}
                      type="button"
                      onClick={() => { setTone(tn.id); regenerate(language, tn.id); }}
                      data-testid={`email-tone-${tn.id}`}
                      className={
                        "h-9 rounded-lg text-[11px] font-bold transition-colors border " +
                        (active
                          ? "bg-[#8B5CF6] text-white border-[#8B5CF6]"
                          : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#8B5CF6]")
                      }
                    >
                      {uiLabel(tn)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-[#6B7280]">
              <Loader2 className="w-7 h-7 animate-spin text-[#3B82F6]" />
              <p className="text-[13px]">{headerText.drafting}</p>
            </div>
          ) : (
            <>
              {/* Recipient — editable, auto-discovered when the lead has no email */}
              <div>
                <label className="block text-[12px] font-semibold text-[#6B7280] mb-1.5">
                  {language === "pt" ? "Para (destinatário)" : language === "es" ? "Para (destinatario)" : "To (recipient)"}
                </label>
                <div className="relative">
                  <input
                    data-testid="email-to-input"
                    type="email"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    placeholder={discovering
                      ? (language === "pt" ? "Buscando e-mail no site do lead..." : "Searching lead's website for email...")
                      : (language === "pt" ? "email@empresa.com — não encontrado, digite manualmente" : "email@company.com")}
                    className={`w-full h-11 px-3.5 pr-24 rounded-lg border bg-white focus:outline-none focus:ring-2 text-[14px] ${
                      to ? "border-[#10B981] focus:border-[#10B981] focus:ring-[#10B981]/15"
                        : "border-[#F59E0B] focus:border-[#3B82F6] focus:ring-[#3B82F6]/15"}`}
                  />
                  {discovering && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#3B82F6]" data-testid="email-discovering-badge">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      {language === "pt" ? "buscando..." : "finding..."}
                    </span>
                  )}
                  {!discovering && discoverySource && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-wide text-[#10B981] bg-[#ECFDF5] px-2 py-0.5 rounded-full" data-testid="email-discovery-source">
                      {discoverySource === "website" ? (language === "pt" ? "achado no site" : "found on site") : discoverySource}
                    </span>
                  )}
                </div>
                {!to && !discovering && (
                  <p className="text-[11px] text-[#B45309] mt-1">
                    {language === "pt"
                      ? "Nenhum e-mail encontrado automaticamente. Digite acima ou use 'Copiar email' para enviar por outro canal."
                      : "No email found automatically. Type one above or copy the email to send via another channel."}
                  </p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[12px] font-semibold text-[#6B7280]">{headerText.subject}</label>
                  <div className="relative">
                    <select
                      data-testid="email-template-picker"
                      onChange={(e) => {
                        const tpl = EMAIL_TEMPLATES.find((t) => t.id === e.target.value);
                        if (!tpl) return;
                        setSubject(applyPlaceholders(tpl.subject, lead));
                        setBody(applyPlaceholders(tpl.body, lead));
                        toast.success(language === "pt" ? `Template ${tpl.niche} aplicado` : language === "es" ? `Plantilla ${tpl.niche} aplicada` : `${tpl.niche} template applied`);
                        e.target.value = "";
                      }}
                      defaultValue=""
                      className="text-[11px] font-bold px-2 py-1 rounded border border-[#A855F7] bg-[#FAF5FF] text-[#7C3AED] cursor-pointer hover:bg-[#F3E8FF] focus:outline-none">
                      <option value="" disabled>
                        {language === "pt" ? "+ Template" : language === "es" ? "+ Plantilla" : "+ Template"}
                      </option>
                      {EMAIL_TEMPLATES.map((t) => (
                        <option key={t.id} value={t.id}>{t.niche}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <input
                  data-testid="email-subject-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full h-11 px-3.5 rounded-lg border border-[#E5E7EB] bg-white focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/15 text-[14px]"
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#6B7280] mb-1.5">{headerText.body}</label>
                <textarea
                  data-testid="email-body-textarea"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  className="w-full p-3.5 rounded-lg border border-[#E5E7EB] bg-white focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/15 text-[14px] leading-relaxed resize-y"
                />
              </div>

              {/* Iter51 — Attachment slot (PDF / CSV / image). */}
              <div className="rounded-lg border border-dashed border-[#E5E7EB] bg-[#F9FAFB] p-3" data-testid="email-attachment-box">
                {!attachment ? (
                  <label className="flex items-center justify-between gap-2 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Paperclip className="w-4 h-4 text-[#6B7280]" />
                      <span className="text-[12.5px] text-[#6B7280] font-medium">
                        {language === "pt" ? "Anexar arquivo (PDF, CSV, imagem)" : language === "es" ? "Adjuntar archivo (PDF, CSV, imagen)" : "Attach a file (PDF, CSV, image)"}
                      </span>
                    </div>
                    <span className="text-[10.5px] uppercase font-bold tracking-wider text-[#3B82F6]">
                      {language === "pt" ? "Escolher" : language === "es" ? "Elegir" : "Choose"}
                    </span>
                    <input
                      type="file"
                      data-testid="email-attachment-input"
                      accept=".pdf,.csv,.txt,.png,.jpg,.jpeg,.webp,.docx,.xlsx"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 10 * 1024 * 1024) {
                          toast.error(language === "pt" ? "Arquivo > 10 MB. Use um menor." : "File > 10 MB. Use a smaller one.");
                          e.target.value = "";
                          return;
                        }
                        setAttachment({ name: f.name, size: f.size, type: f.type, file: f });
                        e.target.value = "";
                      }}
                    />
                  </label>
                ) : (
                  <div className="flex items-center justify-between gap-2" data-testid="email-attachment-row">
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="w-4 h-4 text-[#3B82F6] shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-semibold text-[#111827] truncate">{attachment.name}</div>
                        <div className="text-[10.5px] text-[#6B7280]">
                          {(attachment.size / 1024).toFixed(1)} KB · {attachment.type || "file"}
                        </div>
                      </div>
                    </div>
                    <button type="button" onClick={() => setAttachment(null)}
                      data-testid="email-attachment-remove"
                      className="text-[#6B7280] hover:text-[#DC2626] p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {attachment && (
                  <p className="text-[10.5px] text-[#9CA3AF] italic mt-2">
                    {language === "pt"
                      ? "Aviso: o link 'Enviar via Gmail' usa mailto: e não anexa arquivos automaticamente. Você precisa anexar manualmente na janela do Gmail."
                      : language === "es"
                      ? "Aviso: el enlace 'Enviar por Gmail' usa mailto: y no adjunta archivos automáticamente."
                      : "Heads-up: the 'Send via Gmail' link uses mailto: and won't attach files automatically. Attach it inside Gmail."}
                  </p>
                )}
              </div>

              {/* AI Enhance panel */}
              <div className="rounded-lg border border-dashed border-[#A855F7] bg-[#FAF5FF] p-3 space-y-2" data-testid="email-ai-panel">
                <div className="text-[11.5px] font-bold text-[#7C3AED] inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> {headerText.ai_help}
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    placeholder={headerText.ai_placeholder}
                    data-testid="email-ai-instruction"
                    className="flex-1 al-input bg-white h-9 px-3 text-[12.5px]"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); enhance(); } }}
                  />
                  <button
                    type="button"
                    onClick={() => enhance()}
                    disabled={enhancing || !instruction.trim()}
                    data-testid="email-ai-apply"
                    className="h-9 px-3 rounded-lg bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {enhancing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {headerText.ai_apply}
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mr-1 self-center">{headerText.ai_presets}:</span>
                  {(AI_SUGGESTIONS[language] || AI_SUGGESTIONS.en).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => enhance(s)}
                      disabled={enhancing}
                      data-testid={`email-ai-preset-${s.replace(/\s+/g, "-").toLowerCase()}`}
                      className="text-[10.5px] px-2 py-0.5 rounded-full border border-[#A855F7] text-[#7C3AED] bg-white hover:bg-[#F3E8FF] font-semibold"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  data-testid="email-regen-btn"
                  onClick={() => regenerate()}
                  disabled={!onRegenerate}
                  className="flex items-center gap-1.5 px-3 h-10 rounded-lg border border-[#E5E7EB] text-[13px] font-medium hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors disabled:opacity-50"
                >
                  <RefreshCw className="w-4 h-4" /> {headerText.regen}
                </button>
                <button
                  data-testid="email-copy-btn"
                  onClick={copy}
                  className="flex items-center gap-1.5 px-4 h-10 rounded-lg border border-[#E5E7EB] text-[13px] font-medium hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
                >
                  <Copy className="w-4 h-4" /> {headerText.copy}
                </button>
                <button
                  data-testid="email-send-gmail-btn"
                  onClick={sendGmail}
                  className="flex items-center gap-1.5 px-3 h-10 rounded-lg border border-[#E5E7EB] text-[13px] font-medium hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
                >
                  <Send className="w-4 h-4" /> {headerText.send}
                </button>
                <button
                  data-testid="email-send-resend-btn"
                  onClick={sendResend}
                  disabled={sending}
                  className="al-btn-primary flex items-center gap-1.5 px-4 h-10 rounded-lg text-[13px] font-bold bg-[#10B981] hover:bg-[#059669] text-white disabled:opacity-60"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {headerText.send_resend}
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
